/**
 * drillUrl.ts — BUILD-14: URL drill-down helpers.
 *
 * Pattern: hash-router query params (?key=value) embedded after the hash path.
 * Works with wouter's useHashLocation router.
 *
 * Usage:
 *   setLocation(buildDrillUrl('/pipeline', { owner: userId, stage: 'Proposal' }));
 *   window.location.hash = buildDrillUrl('/pipeline', { owner: userId });
 */

/**
 * Build a hash-router URL with query params stripped of undefined/empty values.
 *
 * @param base  e.g. '/pipeline' or '/activity/drilldown'
 * @param params key→value map; undefined/null/'' values are omitted
 */
export function buildDrillUrl(
  base: string,
  params: Record<string, string | number | null | undefined>
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (!entries.length) return base;
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v!))}`)
    .join("&");
  return `${base}?${qs}`;
}

/**
 * Parse hash query params from the current URL.
 * Reads window.location.hash and extracts the ?key=value segment.
 *
 * Returns a plain object of decoded string values.
 */
export function parseDrillParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const hash = window.location.hash;
  const qIdx = hash.indexOf("?");
  if (qIdx === -1) return {};
  const raw = hash.slice(qIdx + 1);
  const out: Record<string, string> = {};
  raw.split("&").forEach((part) => {
    const eqIdx = part.indexOf("=");
    if (eqIdx === -1) return;
    const k = decodeURIComponent(part.slice(0, eqIdx));
    const v = decodeURIComponent(part.slice(eqIdx + 1));
    if (k) out[k] = v;
  });
  return out;
}

/**
 * Typed drill params interface used across all dashboard drill-downs.
 */
export interface DrillParams {
  // Pipeline filters
  owner?: string;
  stage?: string;
  period?: string;
  gap?: string;

  // Activity audit filters
  kpi?: string;
  window?: string;
  role?: string;
  rep?: string;
  label?: string;

  // Marketing filters
  channel?: string;
}

/**
 * Parse and return typed drill params from the current URL.
 */
export function useDrillParams(): DrillParams {
  return parseDrillParams() as DrillParams;
}
